/**
 * Host auth bridge for Module Federation (MF) deployments.
 *
 * When NG Meet runs as an MF remote inside TeamHub, the host has already
 * authenticated the user via `@star2star/cpaas-pkce-auth` and exposes it as
 * `window.PKCEAuth`. TeamHub also exposes `window.appService` (a
 * `@star2star/scuba` instance) but that surface is a REGISTRY of
 * token-producer setters (`setGetIdTokenFunc`, …) — it does NOT itself
 * expose `getIdToken` / `getAccessToken`. The actual token reader lives on
 * `window.PKCEAuth`. See `teamhub/react/src/bootstrap.js` lines 16, 54-56
 * (registration) and 76 (`PKCEAuth.getAccessToken()` call site).
 *
 * NG Meet must NOT run its own `@sangoma/react-oauth2-code-pkce` PKCE flow
 * in that environment — it would attempt a second OAuth redirect, conflict
 * with the host's tokens, and corrupt `localStorage`. Instead, the hosted
 * path reads tokens from this bridge.
 *
 * Standalone NG Meet never touches this module.
 *
 * MODE DETECTION: We decide "hosted vs standalone" exactly once, at startup,
 * from a single definitive signal — WHICH ENTRY POINT RAN. The exposed
 * Module Federation `mount()` (imported by TeamHub via `NgMeet/app`) declares
 * hosted mode by calling `setFederatedMode(true)`; the standalone entry
 * (`main.tsx`, which only executes when the bundle is loaded directly) calls
 * `setFederatedMode(false)`. We deliberately do NOT sniff the DOM, build-time
 * env vars, or repeatedly probe `window.PKCEAuth`: the app was either loaded
 * as a federated remote or it wasn't, and that fact never changes during the
 * page's lifetime.
 */

/**
 * Subset of `@star2star/cpaas-pkce-auth`'s static surface that NG Meet
 * actually uses. We don't import the package directly to avoid a runtime
 * dep — the shape is locked by TeamHub's call sites in its bootstrap
 * (`PKCEAuth.getAccessToken`, `PKCEAuth.getIdToken`, `PKCEAuth.logout`,
 * `PKCEAuth.isAuthorized`).
 */
interface HostPkceAuth {
  getAccessToken?: () => Promise<string> | string;
  getIdToken?: () => Promise<string> | string;
  logout?: () => void;
  isAuthorized?: () => boolean;
}

declare global {
  interface Window {
    PKCEAuth?: HostPkceAuth;
    /**
     * Scuba `appService` registry. Kept typed for completeness but NG Meet
     * does not consume it directly — its public surface is setters, not
     * token getters.
     */
    appService?: {
      reset?: () => void;
      setGetIdTokenFunc?: (fn: () => Promise<string> | string) => void;
      setGetTokenFunc?: (fn: () => Promise<string> | string) => void;
      setLogoutFunc?: (fn: () => void) => void;
    };
  }
}

const getHostPkceAuth = (): HostPkceAuth | undefined =>
  typeof window !== 'undefined' ? window.PKCEAuth : undefined;

/**
 * Whether NG Meet was loaded as a TeamHub-hosted MF remote. Set once by
 * `setFederatedMode()` at startup and never re-evaluated. Defaults to
 * `false` so any consumer that runs before initialization (or in a test
 * harness) safely sees "standalone".
 */
let federatedMode = false;

/**
 * Record — once — whether NG Meet is running as a TeamHub-hosted MF remote,
 * for `isHostedByTeamHub()` to read.
 *
 * The signal is which entry point ran: the exposed MF `mount()` calls this
 * with `true` (TeamHub imported `NgMeet/app` and is driving the mount); the
 * standalone `main.tsx` entry calls it with `false`. In the manifest-based MF
 * model there is no `#root-meet` container to sniff — the host hands us its
 * own element via `mount(el, ...)`.
 *
 * Call this BEFORE `mount()` renders so every downstream consumer of
 * `isHostedByTeamHub()` observes the settled value.
 */
export const setFederatedMode = (hosted: boolean): boolean => {
  federatedMode = hosted;
  return federatedMode;
};

/**
 * True iff NG Meet was loaded as an MF remote by TeamHub. Reflects the
 * one-time decision recorded by `setFederatedMode()`.
 */
export const isHostedByTeamHub = (): boolean => federatedMode;

/**
 * Whether the host's token getters are callable right now.
 *
 * The synchronous counterpart to `waitForHostAuth`, for callers that must
 * decide immediately whether a host identity can be supplied — the inter-module
 * launch API declines rather than opening a meeting that would then fail its
 * auth handshake (SM-1254). Says nothing about whether a user is signed in,
 * only that the bridge exists.
 */
export const hasHostAuthBridge = (): boolean => typeof getHostPkceAuth()?.getIdToken === 'function';

/**
 * Wait for the host's `PKCEAuth` token getters to be callable.
 *
 * This is a token-READINESS check, not a mode check — mode is already settled
 * by `setFederatedMode()`. In the normal TeamHub flow `window.PKCEAuth` (and
 * its methods) are installed synchronously at the top of the host bootstrap,
 * well before our script runs, so this resolves on the first tick. The poll
 * is retained purely as defense for the MS Teams (`msteams`) container
 * variant and any inject-then-initialize ordering quirk.
 *
 * Returns `false` immediately in standalone mode (no host bridge to wait for).
 */
export const waitForHostAuth = async (timeoutMs = 3000): Promise<boolean> => {
  if (!federatedMode) {
    return false;
  }
  const isReady = (): boolean => typeof getHostPkceAuth()?.getAccessToken === 'function';
  if (isReady()) {
    return true;
  }
  const intervalMs = 100;
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, intervalMs));
    if (isReady()) {
      return true;
    }
  }
  return false;
};

/**
 * Fetch the host's current access (cpaas) token. Always re-asks the host —
 * never caches — because `@star2star/cpaas-pkce-auth` rotates the token
 * under us and a cached string would 401 after the rotation window.
 */
export const getAccessToken = async (): Promise<string | null> => {
  const pkce = getHostPkceAuth();
  if (typeof pkce?.getAccessToken !== 'function') {
    return null;
  }
  try {
    const token = await pkce.getAccessToken();
    return token || null;
  } catch (err) {
    console.warn('[hostAuth] getAccessToken failed', err);
    return null;
  }
};

/**
 * Fetch the host's current id token. NG Meet's own backend uses the id token
 * (not the access token) as its bearer — see `useApiManager`.
 */
export const getIdToken = async (): Promise<string | null> => {
  const pkce = getHostPkceAuth();
  if (typeof pkce?.getIdToken !== 'function') {
    return null;
  }
  try {
    const token = await pkce.getIdToken();
    return token || null;
  } catch (err) {
    console.warn('[hostAuth] getIdToken failed', err);
    return null;
  }
};

/**
 * Trigger the host's logout flow. The host (TeamHub) tears down its own
 * session and reloads, which also unmounts NG Meet. Prefer `window.logout`
 * if TeamHub installed it (it bundles `PKCEAuth.logout()` + `appService.reset()`),
 * otherwise fall back to the bare `PKCEAuth.logout()`.
 */
export const logout = (): void => {
  const composedLogout = (window as unknown as { logout?: () => void }).logout;
  if (typeof composedLogout === 'function') {
    try {
      composedLogout();
      return;
    } catch (err) {
      console.warn('[hostAuth] window.logout failed; falling back to PKCEAuth.logout', err);
    }
  }
  const pkce = getHostPkceAuth();
  if (typeof pkce?.logout === 'function') {
    try {
      pkce.logout();
    } catch (err) {
      console.warn('[hostAuth] PKCEAuth.logout failed', err);
    }
  }
};
